<template>
  <div class="export-metrics-wrapper">
    <ActionButton
      variant="success"
      :loading="isLoading"
      loading-text="Exporting..."
      custom-class="export-metrics-button"
      @click="exportMetrics"
    >
      <template #icon-left>
        <img
          src="/images/admin/download.svg"
          alt=""
          class="export-metrics-button__icon"
        />
      </template>
      Export Metrics
    </ActionButton>
  </div>
  <div class="border-container-unique38 my-4">
    <div class="admin-content-layout">
      <div
        class="admin-main-content"
        style="
          padding-top: 0px;
          max-width: 100%;
          padding-right: 0px;
          overflow: hidden;
        "
      >
        <div class="tab-menu">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            type="button"
            class="tab-item"
            :class="{ 'tab-item--active': activeTab === tab.id }"
            @click="setActiveTab(tab.id)"
          >
            {{ tab.label }}
          </button>
        </div>

        <div class="chart-checkboxes">
          <label class="chart-toggle">
            <input
              v-model="showNew"
              type="checkbox"
              class="chart-toggle__input"
            />
            <span class="chart-toggle__box"></span>
            <span class="chart-toggle__label">
              {{ activeTabMeta.newLabel }}
            </span>
            <span
              class="chart-toggle__line chart-toggle__line--solid"
              :style="{ backgroundColor: activeTabMeta.color }"
            ></span>
          </label>

          <label class="chart-toggle">
            <input
              v-model="showCumulative"
              type="checkbox"
              class="chart-toggle__input"
            />
            <span class="chart-toggle__box"></span>
            <span class="chart-toggle__label">
              {{ activeTabMeta.cumulativeLabel }}
            </span>
            <span
              class="chart-toggle__line chart-toggle__line--dashed"
              :style="{ color: activeTabMeta.color }"
            ></span>
          </label>
        </div>

        <div>
          <AreaChart
            :series="visibleSeries"
            :categories="activeTabMeta.categories.value"
            :color1="activeTabMeta.color"
            :color2="activeTabMeta.color"
            :dash-array="activeDashArray"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import sfiService, { sfiStore } from "@/services/sfiService";
import AreaChart from "@/components/admin/sfi/AreaChart.vue";
import ActionButton from "@/components/common/ActionButton.vue";
import { computed, onMounted, ref } from "vue";
import dayjs from "dayjs";

const isLoading = ref(false);
const activeTab = ref("total-sessions");
const showNew = ref(true);
const showCumulative = ref(true);

const formatPeriodLabel = (period) => {
  if (!period) return "";
  const hyphenCount = (period.match(/-/g) || []).length;
  if (hyphenCount >= 2) return dayjs(period).format("MMM D");
  if (hyphenCount === 1) return dayjs(period).format("MMM YYYY");
  return dayjs(period).format("YYYY");
};

// chart data
const chartData = ref([
  {
    name: "New Sessions",
    data: [0, 0],
  },
  {
    name: "Cumulative Sessions",
    data: [0, 0],
  },
]);
const chartCategories1 = ref(["-", "-"]);

// chart data 2
const chartData2 = ref([
  {
    name: "New Sessions",
    data: [0, 0],
  },
  {
    name: "Cumulative Sessions",
    data: [0, 0],
  },
]);
const chartCategories2 = ref(["-", "-"]);

// chart data 3
const chartData3 = ref([
  {
    name: "New Sessions",
    data: [0, 0],
  },
  {
    name: "Cumulative Sessions",
    data: [0, 0],
  },
]);
const chartCategories3 = ref(["-", "-"]);

// chart data 4 - Students
const chartData4 = ref([
  {
    name: "New Students",
    data: [0, 0],
  },
  {
    name: "Cumulative Students",
    data: [0, 0],
  },
]);
const chartCategories4 = ref(["-", "-"]);

const tabs = [
  {
    id: "total-sessions",
    label: "Total Sessions",
    description: "Total sessions over the selected period",
    newLabel: "New Sessions",
    cumulativeLabel: "Cumulative Sessions",
    color: "#00925E",
    chartData: chartData,
    categories: chartCategories1,
  },
  {
    id: "total-distinct-educators",
    label: "Total Distinct Educators",
    description: "Distinct educators over the selected period",
    newLabel: "New Educators",
    cumulativeLabel: "Cumulative Educators",
    color: "#E00069",
    chartData: chartData2,
    categories: chartCategories2,
  },
  {
    id: "total-assigned-sp",
    label: "Total Assignments",
    description: "Total assignments over the selected period",
    newLabel: "New Assignments",
    cumulativeLabel: "Cumulative Assignments",
    color: "#0068B5",
    chartData: chartData3,
    categories: chartCategories3,
  },
  {
    id: "total-students",
    label: "Total Distinct Students",
    description: "Distinct students over the selected period",
    newLabel: "New Students",
    cumulativeLabel: "Cumulative Students",
    color: "#6F2DBD",
    chartData: chartData4,
    categories: chartCategories4,
  },
];

const activeTabMeta = computed(
  () => tabs.find((t) => t.id === activeTab.value) ?? tabs[0]
);

const visibleSeries = computed(() => {
  const all = activeTabMeta.value.chartData.value ?? [];
  return all.filter((_, i) => (i === 0 ? showNew.value : showCumulative.value));
});

/** Build dashArray that matches the *visible* series order */
const activeDashArray = computed(() => {
  const dash = [];
  if (showNew.value) dash.push(0);        // solid line
  if (showCumulative.value) dash.push(4); // dashed line
  return dash;
});

// set active tab
const setActiveTab = (tab) => {
  activeTab.value = tab;
};

const exportMetrics = async () => {
  isLoading.value = true;

  try {
    // Use the existing HTTP service which already handles authentication
    const response = await sfiService.exportMetrics(
      sfiStore.startDate,
      sfiStore.endDate
    );

    // Create a blob from the response data
    const blob = new Blob([response.data], {
      type: response.headers["content-type"] || "text/csv",
    });

    // Create an object URL for the blob
    const url = window.URL.createObjectURL(blob);

    // Create a temporary link element
    const link = document.createElement("a");
    link.href = url;

    // Get filename from Content-Disposition header or use default with date range
    const contentDisposition = response.headers["content-disposition"];
    const startDateFormatted = dayjs(sfiStore.startDate).format("YYYY-MM-DD");
    const endDateFormatted = dayjs(sfiStore.endDate).format("YYYY-MM-DD");
    const filename = contentDisposition
      ? contentDisposition.split("filename=")[1].replace(/"/g, "")
      : `sfi-metrics-${startDateFormatted}_to_${endDateFormatted}.csv`;

    link.setAttribute("download", filename);
    document.body.appendChild(link);

    // Trigger click to download
    link.click();

    // Clean up
    window.URL.revokeObjectURL(url);
    document.body.removeChild(link);
  } catch (error) {
    console.error("Error exporting metrics:", error);
    alert("Failed to export metrics. Please try again.");
  } finally {
    isLoading.value = false;
  }
};

// Fetch data for the selected tab
const getMetricsData = async () => {
  if (sfiStore.loading) return;

  if (
    sfiStore.data &&
    sfiStore.data.dataTrends &&
    sfiStore.data.dataTrends.totalSessions
  ) {
    const totalSessions = sfiStore.data.dataTrends.totalSessions;
    chartData.value = [
      {
        name: "New Sessions",
        data: totalSessions.map((item) => item.count),
      },
      {
        name: "Cumulative Sessions",
        data: totalSessions.map((item) => item.cumulativeCount),
      },
    ];
    chartCategories1.value = totalSessions.map((item) =>
      formatPeriodLabel(item.period)
    );

    // Similar processing for other tabs' data
    if (sfiStore.data.dataTrends.totalDistinctUsers) {
      const totalEducators = sfiStore.data.dataTrends.totalDistinctUsers;
      chartData2.value = [
        {
          name: "New Educators",
          data: totalEducators.map((item) => item.count),
        },
        {
          name: "Cumulative Educators",
          data: totalEducators.map((item) => item.cumulativeCount),
        },
      ];
      chartCategories2.value = totalEducators.map((item) =>
        formatPeriodLabel(item.period)
      );
    }

    if (sfiStore.data.dataTrends.totalAssignments) {
      const totalPacks = sfiStore.data.dataTrends.totalAssignments;
      chartData3.value = [
        {
          name: "New Assignments",
          data: totalPacks.map((item) => item.count),
        },
        {
          name: "Cumulative Assignments",
          data: totalPacks.map((item) => item.cumulativeCount),
        },
      ];
      chartCategories3.value = totalPacks.map((item) =>
        formatPeriodLabel(item.period)
      );
    }

    if (sfiStore.data.dataTrends.totalStudents) {
      const totalStudents = sfiStore.data.dataTrends.totalStudents;
      chartData4.value = [
        {
          name: "New Students",
          data: totalStudents.map((item) => item.count),
        },
        {
          name: "Cumulative Students",
          data: totalStudents.map((item) => item.cumulativeCount),
        },
      ];
      chartCategories4.value = totalStudents.map((item) =>
        formatPeriodLabel(item.period)
      );
    }
  }
};

onMounted(() => {
  getMetricsData();
});
</script>

<style scoped>
.admin-header {
  cursor: pointer;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.4);
}

.activeChartContent {
  color: black;
  font-weight: 600;
}

.tab-menu {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0 12px;
  margin-bottom: 8px;
}

.tab-item {
  flex: 1 0 0;
  min-width: 0;
  padding: 16px;
  background: transparent;
  border: none;
  border-bottom: 1px solid var(--neutral-700);
  cursor: pointer;
  font-family: var(--font-family-primary);
  font-size: var(--font-size-title-big);
  line-height: var(--line-height-title-big);
  font-weight: var(--font-weight-regular);
  letter-spacing: 0.15px;
  color: var(--black-body-text);
  text-align: center;
  white-space: nowrap;
  transition:
    color 0.15s ease,
    border-color 0.15s ease;
}

.tab-item:hover:not(.tab-item--active) {
  color: var(--primary-blue);
}

.tab-item--active {
  color: var(--primary-blue);
  font-weight: var(--font-weight-bold);
  border-bottom: 4px solid var(--primary-blue);
}

.chart-checkboxes {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 12px;
  margin-bottom: 4px;
}

/* ── Custom checkbox toggle ── */
.chart-toggle {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  user-select: none;
}

.chart-toggle__input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.chart-toggle__box {
  position: relative;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border: 2px solid var(--primary-blue);
  border-radius: 4px;
  background: #fff;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.chart-toggle__input:checked ~ .chart-toggle__box {
  background-color: var(--primary-blue);
  border-color: var(--primary-blue);
}

/* checkmark */
.chart-toggle__box::after {
  content: "";
  position: absolute;
  display: none;
  left: 6px;
  top: 2px;
  width: 6px;
  height: 12px;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.chart-toggle__input:checked ~ .chart-toggle__box::after {
  display: block;
}

.chart-toggle__label {
  color: var(--primary-blue);
  font-family: var(--font-family-primary);
  font-size: var(--font-size-title-medium);
  line-height: var(--line-height-title-medium);
  font-weight: var(--font-weight-medium);
  letter-spacing: 0.024px;
  white-space: nowrap;
}

/* ── Line indicators next to label ── */
.chart-toggle__line {
  display: inline-block;
  width: 24px;
  height: 2px;
  flex-shrink: 0;
}

.chart-toggle__line--solid {
  /* backgroundColor set via :style binding */
}

.chart-toggle__line--dashed {
  background-image: linear-gradient(
    to right,
    currentColor 4px,
    transparent 4px
  );
  background-size: 7px 2px;
  background-repeat: repeat-x;
  background-color: transparent;
}

.export-metrics-wrapper {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}

:deep(.export-metrics-button) {
  padding: 10px 16px;
  min-height: 0;
}

.export-metrics-button__icon {
  width: 24px;
  height: 24px;
  filter: brightness(0) invert(1);
}
</style>
